/* ===========================================================================
   Neu — shared accordion open/close motion.

   STANDING RULE: every accordion on the site animates open and closed. This is
   the one place that happens. See neu-accordion.css for why it is JS and not
   `::details-content` (short version: that approach silently clamps the panel
   shut when the <details> is a flex item, which is the case on the FAQ bands).

   Design constraints this respects:
   - Never strand a panel. Every path ends with the inline height cleared and the
     native `open` attribute in the state the user asked for. If the script throws
     halfway, the browser's own toggle has still happened.
   - Never fight a section's own JS. Sections that enforce single-open (neu-pdp-faq,
     neu-warranty-faq) do it by setting `other.open = false` directly. That is a
     programmatic change, not a click, so it is left alone and simply snaps —
     correct behaviour, since animating a panel the user did not touch would read
     as jitter.
   - Never touch the collection filters (`.neu-filters__group`). They have their own
     motion and the filter JS measures heights.
   =========================================================================== */
(function () {
  var SEL = [
    '.faq__item',
    '.neu-ff__item',
    '.neu-pfaq__item',
    '.neu-wf__item',
    '.neu-pspec__card',
    '.neu-pdesc__card',
    '.neu-cmsu__item',
    '.pdp__accordion',
    '[data-neu-accordion]'
  ].join(',');

  var DUR = 280;
  var EASE = 'cubic-bezier(.4,0,.2,1)';
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function mark(root) {
    var list;
    try { list = (root || document).querySelectorAll(SEL); } catch (e) { return; }
    for (var i = 0; i < list.length; i++) {
      var d = list[i];
      if (d.tagName !== 'DETAILS') continue;
      if (d.closest('.neu-filters__group')) continue;
      d.setAttribute('data-neu-acc', '');
    }
  }

  function summaryOf(d) {
    for (var n = d.firstElementChild; n; n = n.nextElementSibling) {
      if (n.tagName === 'SUMMARY') return n;
    }
    return null;
  }

  // A section that already animates its own panel (neu-warranty-faq does, via
  // ::details-content) is left entirely alone - doubling up fights it and, worse,
  // its clamped panel measures as zero-height so we would mis-read it as empty.
  function ownsItsMotion(d) {
    try {
      var t = getComputedStyle(d, '::details-content').transitionDuration || '';
      return t.split(',').some(function (v) { return parseFloat(v) > 0; });
    } catch (e) { return false; }
  }

  function run(d, opening) {
    var sum = summaryOf(d);
    if (!sum) return false;
    if (ownsItsMotion(d)) return false;

    var wasOpen = d.open;
    var from = d.getBoundingClientRect().height;

    // Measure the target with the panel laid out but no inline height in the way.
    d.open = true;
    d.style.height = '';
    var openH = d.getBoundingClientRect().height;
    var shutH = sum.getBoundingClientRect().height;
    var to = opening ? openH : shutH;

    // Nothing worth animating (empty panel, or we cannot measure it). Put the
    // element back exactly as it was and let the browser's own toggle proceed -
    // do NOT set `open` here, or it fights the native toggle we are declining.
    if (Math.abs(to - from) < 2) {
      d.open = wasOpen;
      d.style.height = '';
      return false;
    }

    d.setAttribute('data-neu-acc-run', '');
    d.style.height = from + 'px';
    // force a style resolve so the browser has a start value to animate from
    void d.offsetHeight;
    d.style.transition = 'height ' + DUR + 'ms ' + EASE;
    d.style.height = to + 'px';

    var done = false;
    function finish() {
      if (done) return;
      done = true;
      d.removeEventListener('transitionend', onEnd);
      d.style.transition = '';
      d.style.height = '';
      d.removeAttribute('data-neu-acc-run');
      d.open = opening;          // last, so a closing panel is visible throughout
    }
    function onEnd(e) {
      if (e.target === d && e.propertyName === 'height') finish();
    }
    d.addEventListener('transitionend', onEnd);
    setTimeout(finish, DUR + 120);   // belt and braces if transitionend never fires
    return true;
  }

  document.addEventListener('click', function (e) {
    if (reduce) return;
    var sum = e.target && e.target.closest && e.target.closest('summary');
    if (!sum) return;
    var d = sum.parentElement;
    if (!d || d.tagName !== 'DETAILS' || !d.hasAttribute('data-neu-acc')) return;
    if (d.hasAttribute('data-neu-acc-run')) { e.preventDefault(); return; }
    // let the browser handle anything that is not a plain activation
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var opening = !d.open;
    if (run(d, opening)) e.preventDefault();
  }, true);

  function init() { mark(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Sections re-render in the theme editor, and some panels are injected late.
  document.addEventListener('shopify:section:load', function (e) { mark(e.target); });
})();
